import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Loader2, KeyRound, CheckCircle2 } from 'lucide-react';
import { formatApiError } from '../auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const search = useLocation().search;
  const navigate = useNavigate();
  const token = useMemo(() => new URLSearchParams(search).get('token') || '', [search]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password || loading) return;
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords don’t match.'); return; }
    setLoading(true); setError(null);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/admin'), 1800);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-[color:var(--mv-navy)] px-4">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8 text-center">
          <p className="text-sm text-slate-600">This link is missing a token. Please request a new reset link.</p>
          <Link to="/admin" className="mt-3 inline-block text-sm font-bold text-[color:var(--mv-orange)]">← Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-[color:var(--mv-navy)] px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8">
        <div className="w-12 h-12 rounded-full bg-[color:var(--mv-navy)] text-white flex items-center justify-center mx-auto">
          <KeyRound className="w-5 h-5" />
        </div>
        <h1 className="font-display text-2xl font-extrabold text-center text-[color:var(--mv-navy)] mt-4">Set a new password</h1>
        <p className="text-sm text-slate-500 text-center mt-1">Pick a strong password you'll remember.</p>

        {done ? (
          <div className="mt-5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3 inline-flex items-center gap-2 w-full justify-center" data-testid="reset-success">
            <CheckCircle2 className="w-4 h-4" /> Password updated — redirecting you to sign in…
          </div>
        ) : (
          <>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (min 8 chars)"
              className="mt-5 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-[color:var(--mv-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)]"
              autoFocus
              data-testid="reset-password-input"
            />
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              className="mt-3 h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-[color:var(--mv-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--mv-orange)]"
              data-testid="reset-confirm-input"
            />
            {error && <div className="mt-2 text-xs text-red-600 text-center">{error}</div>}
            <button
              type="submit"
              disabled={loading || !password || !confirm}
              data-testid="reset-submit"
              className="mt-4 w-full h-11 rounded-md bg-[color:var(--mv-orange)] hover:bg-orange-600 disabled:opacity-60 text-white text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating…</> : 'Update password'}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
